import React from 'react';
import styles from './Table.module.scss';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  render?: (item: T, index: number) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  /** Hide this column in mobile card view */
  hideOnMobile?: boolean;
  /** Allow cell text to wrap (default table cells use nowrap) */
  wrap?: boolean;
  /** Allow sorting by this column */
  sortable?: boolean;
  /** Custom sort function for complex values */
  sortFn?: (a: T, b: T) => number;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  loading?: boolean;
  className?: string;
  /** Custom render for mobile card view */
  renderMobileCard?: (item: T, index: number) => React.ReactNode;
  /** Current sort configuration */
  sortConfig?: SortConfig;
  /** Called when sort changes */
  onSortChange?: (config: SortConfig) => void;
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'No data available',
  loading = false,
  className = '',
  renderMobileCard,
  sortConfig,
  onSortChange,
}: TableProps<T>) {
  
  const handleSort = (column: Column<T>) => {
    if (!column.sortable || !onSortChange) return;
    
    let direction: SortDirection = 'asc';
    if (sortConfig?.key === column.key) {
      if (sortConfig.direction === 'asc') direction = 'desc';
      else if (sortConfig.direction === 'desc') direction = null;
    }
    
    onSortChange({ key: column.key, direction });
  };
  
  const getSortIndicator = (column: Column<T>) => {
    if (!column.sortable) return null;
    if (sortConfig?.key !== column.key || !sortConfig.direction) {
      return <span className={styles.sortIndicator}>⇅</span>;
    }
    return (
      <span className={styles.sortIndicatorActive}>
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <span>Loading...</span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <span className={styles.emptyIcon}>📋</span>
        <span className={styles.emptyMessage}>{emptyMessage}</span>
      </div>
    );
  }

  // Get columns for mobile card display (exclude hidden ones)
  const mobileColumns = columns.filter(col => !col.hideOnMobile && col.key !== 'actions');

  return (
    <>
      {/* Desktop Table View */}
      <div className={`${styles.tableWrapper} ${className}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{ width: col.width, textAlign: col.align || 'left' }}
                  className={col.sortable ? styles.sortableHeader : ''}
                  onClick={() => handleSort(col)}
                >
                  <span
                    className={`${styles.headerContent} ${
                      col.align === 'right'
                        ? styles.headerContentAlignRight
                        : col.align === 'center'
                          ? styles.headerContentAlignCenter
                          : ''
                    }`}
                  >
                    {col.header}
                    {getSortIndicator(col)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr
                key={keyExtractor(item)}
                onClick={() => onRowClick?.(item)}
                className={onRowClick ? styles.clickableRow : ''}
              >
                {columns.map(col => (
                  <td
                    key={col.key}
                    style={{ textAlign: col.align || 'left' }}
                    className={col.wrap ? styles.cellWrap : undefined}
                  >
                    {col.render
                      ? col.render(item, index)
                      : (item as Record<string, unknown>)[col.key] as React.ReactNode}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className={styles.cardList}>
        {data.map((item, index) => (
          <div
            key={keyExtractor(item)}
            className={`${styles.card} ${onRowClick ? styles.clickableCard : ''}`}
            onClick={() => onRowClick?.(item)}
          >
            {renderMobileCard ? (
              renderMobileCard(item, index)
            ) : (
              <div className={styles.cardContent}>
                {mobileColumns.map(col => (
                  <div key={col.key} className={styles.cardRow}>
                    <span className={styles.cardLabel}>{col.header}</span>
                    <span className={styles.cardValue}>
                      {col.render
                        ? col.render(item, index)
                        : (item as Record<string, unknown>)[col.key] as React.ReactNode}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  /** Optional: Page size options for user selection */
  pageSizeOptions?: readonly number[];
  /** Optional: Called when page size changes */
  onPageSizeChange?: (size: number) => void;
  /** Optional: Custom display text instead of default "Showing X-Y of Z" */
  displayText?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  displayText,
}) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={styles.pagination}>
      <div className={styles.paginationLeft}>
        {displayText ? (
          <span className={styles.paginationInfo}>{displayText}</span>
        ) : (
          <span className={styles.paginationInfo}>
            Showing {startItem}-{endItem} of {totalItems}
          </span>
        )}
        {pageSizeOptions && onPageSizeChange && (
          <div className={styles.pageSizeSelector}>
            <label htmlFor="page-size">Rows:</label>
            <select
              id="page-size"
              value={itemsPerPage}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className={styles.paginationControls}>
        <button
          className={styles.paginationButton}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        >
          Previous
        </button>
        <span className={styles.pageNumber}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className={styles.paginationButton}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Table;

